import { FormControl, Grid } from "@mui/material";
import { ButtonCancel } from "components/buttons";
import { ButtonApplyFilter } from "components/buttons/ButtonApplyFilter";
import { MprcInputStyles } from "components/filter/MprcInputStyles";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { TrainingSearchDuration } from "generated/CoreApi_v1";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAdvFilterPickerProps } from "views/general/AdvancedFilter/interfaces";

export interface WorkloadItem {
    text: string;
    value: TrainingSearchDuration;
}

////////////////////////////////////////////////////////////////////////////////

export const TrainingWorkloadsPicker: React.FC<IAdvFilterPickerProps> = (props) => {

    const { filter, onApply, onCancel } = props;

    const { t } = useTranslation("shared");

    const [selected, setSelected] = useState<AutoCompleteDataModel[]>([]);
    const inputRef = useRef<any>(undefined);
    const multiSelect = filter.canRepeat ?? false;

    const items = useRef<WorkloadItem[]>([
        { text: t("lms:Student.Catalog.Commands.Workload5min"), value: TrainingSearchDuration.Until5 },
        { text: t("lms:Student.Catalog.Commands.Workload5To30Min"), value: TrainingSearchDuration.Between5And30 },
        { text: t("lms:Student.Catalog.Commands.Workload30To60Min"), value: TrainingSearchDuration.Between30And60 },
        { text: t("lms:Student.Catalog.Commands.Workload1To5Hours"), value: TrainingSearchDuration.Between60And300 },
        { text: t("lms:Student.Catalog.Commands.Workload5To10Hours"), value: TrainingSearchDuration.Between300And600 },
        { text: t("lms:Student.Catalog.Commands.WorkloadOther"), value: TrainingSearchDuration.Over600 },
    ]);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();
        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            const filterItems = searchText.length > 0
                ? items.current.filter(opt => opt.text.toLowerCase().includes(searchText))
                : [...items.current];

            resolve({
                items: filterItems.map(x => ({
                    key: x.value,
                    label: x.text,
                    canDelete: true,
                })) as AutoCompleteDataModel[],
                totalRows: filterItems.length
            });
        });
    };

    ///
    const handleApply = () => {
        if (selected.length > 0) {
            const newFilter = selected.map(item => ({
                command: filter!.command,
                optionalGroup: filter?.optionGroup,
                text: `${filter?.displayName.replace("...", "")}: ${item.label}`,
                value: item.key,
                quickFilter: null,
                period: null,
                range: null,
                includeDescendants: null
            }));
            onApply(newFilter, filter);
        }
    };

    ///
    useEffect(() => {
        (inputRef as any)?.current?.focus();
    }, [inputRef]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <Grid container alignItems="flex-end" spacing={2}>
            <Grid size="grow">
                <FormControl fullWidth>
                    <MprcAutoComplete
                        label={filter.placeholder ?? t(`AdvFilter.Picker.Workload.Placeholder${multiSelect ? "_multi" : ""}`)}
                        fullWidth
                        autoFocus
                        multiSelect={multiSelect}
                        inputRef={inputRef}
                        selectedItems={selected}
                        onChange={(items) => setSelected(items)}
                        onGetSuggestions={getSuggestions}
                        onApply={handleApply}
                        onCancel={onCancel}
                        sx={MprcInputStyles.filter}
                    />
                </FormControl>
            </Grid>
            <Grid>
                <ButtonApplyFilter
                    variant="text"
                    disabled={selected.length == 0}
                    onClick={handleApply}
                />
                <ButtonCancel
                    variant="text"
                    onClick={onCancel}
                />
            </Grid>
        </Grid>
    );
};
